// frontend/src/lib/sentry.js
//
// Lazy Sentry loader. The @sentry/react SDK is ~200 KB minified — bundling
// it eagerly inflates the entry chunk and delays FCP/LCP on the landing route
// (which has no errors to capture and no user to identify).
//
// Strategy: export a Sentry-like proxy object that buffers `captureException`
// and `setUser` calls until the real SDK loads. When VITE_SENTRY_DSN is set,
// we kick off the dynamic import on module evaluation so init still happens
// early — but the SDK ends up in its own async chunk, not the entry bundle.

let realSentry = null
const buffered = []

function flush() {
  if (!realSentry) return
  while (buffered.length) {
    const { fn, args } = buffered.shift()
    try {
      const target = realSentry[fn]
      if (typeof target === 'function') target.apply(realSentry, args)
    } catch {
      // Never let a Sentry call mask the original flow.
    }
  }
}

// Public proxy. Only the surface actually used elsewhere in the app —
// captureException, setUser — needs to be implemented. `init` is fired
// here (not by callers) so we don't expose it.
export const Sentry = {
  captureException(...args) {
    if (realSentry) {
      try { realSentry.captureException.apply(realSentry, args) } catch {
        // Never let a Sentry capture error mask the original flow.
      }
    } else {
      buffered.push({ fn: 'captureException', args })
    }
  },
  setUser(...args) {
    if (realSentry) {
      try { realSentry.setUser.apply(realSentry, args) } catch {
        // setUser is best-effort — Sentry may be in a degraded state.
      }
    } else {
      buffered.push({ fn: 'setUser', args })
    }
  },
}

// Kick off the dynamic import synchronously at module-load time. The
// returned promise resolves before module evaluation completes when this
// module is awaited (e.g. tests do `await import('lib/sentry.js')`), so
// init runs deterministically before the awaiter inspects spies.
// Defense-in-depth scrubber: strip credentials from any event before it leaves
// the browser. Matters because logError now forwards handled errors (incl. axios
// errors) to Sentry (W8) — keep Authorization/Cookie/API keys out of the request
// context even if a future integration (e.g. ExtraErrorData) starts attaching it.
export function scrubEvent(event) {
  try {
    const headers = event && event.request && event.request.headers
    if (headers) {
      for (const k of Object.keys(headers)) {
        if (/^(authorization|cookie|x-csrf-token|x-api-key)$/i.test(k)) delete headers[k]
      }
    }
  } catch { /* never block the send */ }
  return event
}

if (import.meta.env.VITE_SENTRY_DSN) {
  const release = import.meta.env.VITE_RELEASE
  // Top-level await — supported by Vite's ESM build target (es2020+).
  // For test envs that mock '@sentry/react', vi.doMock applies before this
  // dynamic import resolves, so the spy gets the init call.
  realSentry = await import('@sentry/react')
  try {
    realSentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      // Filter unexpanded DO App Platform placeholders (e.g. literal "${APP_REVISION}")
      // that appear when VITE_RELEASE is set in app.yaml but DO hasn't expanded the var.
      release: (release && !release.includes('${')) ? release : undefined,
      // Low sample rate — we care about errors, not performance traces
      tracesSampleRate: 0,
      // Ignore ResizeObserver noise (browser quirk, not real errors)
      ignoreErrors: ['ResizeObserver loop limit exceeded'],
      beforeSend: scrubEvent,
    })
  } catch {
    // If init throws (e.g. tests with a partial mock), still let proxy
    // forward subsequent calls to whatever surface the mock exposes.
  }
  flush()
}
